import { __rest } from '../../../../../node_modules/tslib/tslib.es6.mjs.js';
import { evaluate } from '@code2io/common-flow-utilities';
import { faFolderXmark } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import { Rating } from 'primereact/rating';
import { Tag } from 'primereact/tag';
import { TriStateCheckbox } from 'primereact/tristatecheckbox';
import { classNames } from 'primereact/utils';
import React, { useState, useRef, useMemo, useEffect } from 'react';
import { isQueryColumns, getFilterableColumnDataType, getDefaultFilters, mergeVisualSqlWithDataTableFilters } from './tableUtils.js';
import { Button } from 'primereact/button';
import './C2DataTable.css.js';
import { Tooltip } from 'primereact/tooltip';
import { intersectionWith, isEqual } from 'lodash';
import { v5 } from 'uuid';

const C2DataTableChild = (_a) => {
    var _b, _c, _d, _e;
    var { onLoad, onActionButtonClick, columns, sortable, filter, dataSource, dataModelColumns, dataTableVisualSqlQuery, onFilter, mode, actionButtons, loading } = _a, dataTableProps = __rest(_a, ["onLoad", "onActionButtonClick", "columns", "sortable", "filter", "dataSource", "dataModelColumns", "dataTableVisualSqlQuery", "onFilter", "mode", "actionButtons", "loading"]);
    const { value, onLoadCounter } = dataTableProps;
    const [filters, setFilters] = useState({});
    const [sortMeta, setSortMeta] = useState([]);
    const lazyLoadRef = useRef(false);
    const [dynamicValue, setDynamicValue] = useState(value !== null && value !== void 0 ? value : []);
    const tableRef = useRef(null);
    const datatableHeight = ((_e = (_d = (_c = (_b = tableRef.current) === null || _b === void 0 ? void 0 : _b.getElement()) === null || _c === void 0 ? void 0 : _c.getBoundingClientRect()) === null || _d === void 0 ? void 0 : _d.height) !== null && _e !== void 0 ? _e : 0) - 61;
    const limit = datatableHeight > 0 ? Math.floor(datatableHeight / 32) + 5 : 0;
    const [offset, setOffset] = useState(0);
    const inferredColumns = useMemo(() => {
        if (typeof dataModelColumns === "undefined" &&
            typeof columns === "undefined") {
            return [];
        }
        if (typeof dataModelColumns === "undefined") {
            return columns.filter((col) => col.show);
        }
        if (typeof columns === "undefined") {
            return [];
        }
        return columns
            .filter((col) => col.show)
            .map((column) => {
            var _a, _b;
            const dataType = isQueryColumns(dataModelColumns)
                ? (_a = dataModelColumns.find((col) => col.name === column.field)) === null || _a === void 0 ? void 0 : _a.dataType
                : (_b = dataModelColumns.find((col) => col.props.name === column.field)) === null || _b === void 0 ? void 0 : _b.props.dataType;
            return {
                field: column.field,
                header: column.header,
                dataType: getFilterableColumnDataType(dataType),
                displayStyle: column.displayStyle,
                filterable: column.filterable
            };
        });
    }, [columns, dataModelColumns]);
    useEffect(() => {
        if (typeof dataModelColumns === "undefined") {
            return;
        }
        // initialize filters when columns are set
        setFilters(getDefaultFilters(inferredColumns));
    }, [inferredColumns]);
    useEffect(() => {
        if (typeof onLoadCounter !== "undefined" && onLoadCounter !== 0) {
            setDynamicValue([]);
            if (typeof dataTableVisualSqlQuery === "undefined") {
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                    query: null
                }); // legacy
            }
            else if (limit > 0 && mode === "live") {
                console.log("limit", Object.assign(Object.assign({}, dataTableVisualSqlQuery), { limit }));
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                    query: Object.assign(Object.assign({}, dataTableVisualSqlQuery), { limit })
                });
            }
            else if (mode === "design") {
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                    query: dataTableVisualSqlQuery
                });
            }
        }
    }, [onLoadCounter]);
    useEffect(() => {
        if (!value)
            return;
        if (lazyLoadRef.current === false) {
            setDynamicValue([...value]);
            return;
        }
        if (mode === "design") {
            setDynamicValue(value);
        }
        else {
            if (value.length > 0 && intersectionWith(dynamicValue, value, isEqual).length !== value.length) {
                setDynamicValue([...dynamicValue, ...value]);
            }
            else {
                setDynamicValue([...dynamicValue]);
            }
        }
        lazyLoadRef.current = false;
    }, [value]);
    useEffect(() => {
        if (typeof dataTableVisualSqlQuery === "undefined") {
            onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                query: null
            }); // legacy
        }
        else if (limit > 0 && mode === "live") {
            console.log("limit", Object.assign(Object.assign({}, dataTableVisualSqlQuery), { limit }));
            onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                query: Object.assign(Object.assign({}, dataTableVisualSqlQuery), { limit })
            });
        }
        else if (mode === "design") {
            onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                query: dataTableVisualSqlQuery
            });
        }
    }, [dataTableVisualSqlQuery, limit]);
    const noRecordsFound = (React.createElement("div", { className: "w-full flex flex-column text-center gap-2" },
        React.createElement(FontAwesomeIcon, { icon: faFolderXmark, style: {
                fontSize: "1.25rem",
                padding: "0.25rem"
            }, className: "text-gray-300" }),
        React.createElement("p", { className: "text-sm m-0", style: { lineHeight: "1.25rem" } }, !dataSource ? "No Data Source" : "No Records"),
        React.createElement("p", { className: "text-xs m-0", style: { lineHeight: "1rem" } }, !dataSource ? "Please add a data source" : "Please add a record")));
    const getActionColumns = () => {
        return (actionButtons &&
            actionButtons.buttons &&
            actionButtons.buttons.length > 0 && (React.createElement(Column, Object.assign({ key: "actionButtons", header: actionButtons.columnTitle, frozen: actionButtons.freezeColumn }, (actionButtons.freezeColumn
            ? { alignFrozen: actionButtons.columnPosition }
            : {}), { body: (item) => {
                var _a;
                const clickHandler = (e, btnId) => {
                    onActionButtonClick === null || onActionButtonClick === void 0 ? void 0 : onActionButtonClick({
                        rowData: item,
                        buttonIndex: btnId
                    });
                };
                return (_a = actionButtons.buttons) === null || _a === void 0 ? void 0 : _a.map((button) => (React.createElement(Button, { size: "small", className: "mr-2", icon: button.icon, text: true, label: button.label, style: {
                        background: button.buttonColor,
                        color: button.textColor
                    }, onClick: (e) => clickHandler(e, button.id) })));
            } }))));
    };
    const onScroll = (event) => {
        const scrollTop = event.target.scrollTop;
        const scrollHeight = event.target.scrollHeight;
        const height = event.target.clientHeight;
        const scrollBottom = scrollHeight - scrollTop - height;
        if (scrollBottom < 1 && !lazyLoadRef.current) {
            if (typeof dataModelColumns === "undefined") {
                return;
            }
            if (typeof onLoad === "undefined" || (value === null || value === void 0 ? void 0 : value.length) === 0 || (value && (value === null || value === void 0 ? void 0 : value.length) < limit)) {
                return;
            }
            lazyLoadRef.current = true;
            if (typeof dataTableVisualSqlQuery === "undefined") {
                // legacy
                onLoad === null || onLoad === void 0 ? void 0 : onLoad({
                    query: null
                });
            }
            const ultimateLimit = dataTableVisualSqlQuery.limit;
            const newOffset = limit + offset;
            const newLimit = (limit * 2) + offset > ultimateLimit ? ultimateLimit - (offset + limit) : limit;
            if (newLimit <= 0)
                return;
            const sortQuery = mergeVisualSqlWithDataTableFilters(dataTableVisualSqlQuery, filters, dataModelColumns, sortMeta);
            const newQuery = Object.assign(Object.assign({}, sortQuery), { offset: newOffset, limit: newLimit });
            setOffset(newOffset);
            onLoad({
                query: newQuery
            });
        }
    };
    return (React.createElement(DataTable, Object.assign({}, dataTableProps, { lazy: true, ref: tableRef, paginator: false, virtualScrollerOptions: mode === "live" ? {
            onScroll,
            itemSize: 32,
            showLoader: lazyLoadRef.current,
        } : {}, scrollHeight: "flex", value: dynamicValue, scrollable: true, size: "small", emptyMessage: noRecordsFound, multiSortMeta: sortMeta, sortMode: "multiple", onSort: (event) => {
            if (typeof dataModelColumns === "undefined") {
                return;
            }
            if (!event.multiSortMeta) {
                return;
            }
            setSortMeta(event.multiSortMeta);
            const sortQuery = mergeVisualSqlWithDataTableFilters(dataTableVisualSqlQuery, filters, dataModelColumns, event.multiSortMeta);
            setDynamicValue([]);
            onFilter({
                query: Object.assign(Object.assign({}, sortQuery), { limit })
            });
        }, removableSort: true, pt: {
            wrapper: {
                style: {
                    height: dataTableProps.paginator ? "calc(100% - 61px)" : "100%"
                }
            },
            column: {
                bodyCell: {
                    style: {
                        whiteSpace: "nowrap",
                        maxWidth: "300px",
                        minWidth: "300px",
                        textOverflow: "ellipsis",
                        overflow: "hidden"
                    }
                }
            }
        }, className: "c2DataTableChild", loading: loading, filters: filters, onFilter: (value) => {
            if (typeof dataModelColumns === "undefined") {
                return;
            }
            setDynamicValue([]);
            setFilters(value.filters);
            const filterQuery = mergeVisualSqlWithDataTableFilters(dataTableVisualSqlQuery, value.filters, dataModelColumns, sortMeta);
            onFilter({
                query: Object.assign(Object.assign({}, filterQuery), { limit })
            });
        } }),
        (actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.actionColumn) &&
            (actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.columnPosition) === "left" &&
            getActionColumns(),
        inferredColumns.map((col) => {
            var _a, _b;
            return (React.createElement(Column, { key: col.field, body: (item, row) => {
                    if (!item) {
                        return;
                    }
                    const MY_NAMESPACE = '1b671a64-40d5-491e-99b0-da01ff1f3341';
                    const uniqueClassName = `tooltip-${v5(col.field, MY_NAMESPACE)}-${row.rowIndex}`;
                    const isJson = (str) => {
                        try {
                            JSON.parse(str);
                        }
                        catch (e) {
                            return false;
                        }
                        return true;
                    };
                    return (React.createElement(React.Fragment, null,
                        React.createElement(Tooltip, { target: `.${uniqueClassName}`, position: "left", onBeforeShow: () => {
                                const element = document.querySelector(`.${uniqueClassName}`);
                                const spanEl = element === null || element === void 0 ? void 0 : element.firstElementChild;
                                const wrapper = element === null || element === void 0 ? void 0 : element.parentElement;
                                if (element && wrapper && spanEl) {
                                    const elementWidth = spanEl.getBoundingClientRect().width;
                                    const wrapperWidth = wrapper.getBoundingClientRect().width;
                                    if (wrapperWidth < elementWidth) {
                                        return true;
                                    }
                                    else {
                                        return false;
                                    }
                                }
                            }, pt: {
                                root: {
                                    theme: "light",
                                    className: "bg-transparent"
                                }
                            }, autoHide: false },
                            React.createElement("div", { style: {
                                    maxWidth: "300px",
                                    overflow: "auto"
                                } },
                                React.createElement("span", null, typeof getBody(col, item) === "string" &&
                                    isJson(getBody(col, item)) ? (React.createElement("pre", null, JSON.stringify(JSON.parse(getBody(col, item)), null, 4))) : (getBody(col, item))))),
                        React.createElement("div", { className: `tooltip-${col.field}-${row.rowIndex}`, style: {
                                whiteSpace: "nowrap",
                                maxWidth: "300px",
                                textOverflow: "ellipsis",
                                overflow: "hidden"
                            } },
                            React.createElement("span", null, getBody(col, item)))));
                }, className: ((_b = (_a = col.displayStyle) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.alignment)
                    ? `text-${col.displayStyle.options.alignment}`
                    : "text-left", header: col.header, sortField: col.field, sortable: sortable, filter: col.filterable && typeof col.dataType !== "undefined", filterField: col.field, dataType: col.dataType, filterMenuClassName: classNames("tableFilterMenu", {
                    hidden: mode === "design"
                }), filterElement: (options) => {
                    if (typeof col.dataType === "undefined") {
                        return null;
                    }
                    switch (col.dataType) {
                        case "text": {
                            return (React.createElement(InputText, { value: options.value, onChange: (event) => {
                                    options.filterCallback(event.target.value, options.index);
                                } }));
                        }
                        case "numeric": {
                            return (React.createElement(InputNumber, { value: options.value, onChange: (event) => {
                                    options.filterCallback(event.value, options.index);
                                } }));
                        }
                        case "date": {
                            const value = options.value !== null ? new Date(options.value) : null;
                            return (React.createElement(Calendar, { value: value, showTime: true, hourFormat: "12", onChange: (event) => {
                                    if (event.value instanceof Date) {
                                        // https://stackoverflow.com/questions/5129624/convert-js-date-time-to-mysql-datetime
                                        options.filterCallback(event.value.toISOString().replace("T", " "), options.index);
                                    }
                                    else {
                                        console.warn("No associated value type for data table date filter received");
                                    }
                                } }));
                        }
                        case "boolean": {
                            return (React.createElement("div", { className: "flex align-items-center gap-2" },
                                React.createElement("label", null, col.field),
                                React.createElement(TriStateCheckbox, { value: options.value, onChange: (event) => {
                                        options.filterCallback(event.value, options.index);
                                    } })));
                        }
                        default: {
                            console.warn("No matching data type for column with field", col.field);
                            return null;
                        }
                    }
                }, pt: {
                    columnFilter: {
                        className: "ml-1"
                    }
                }, maxConstraints: 99 }));
        }),
        (actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.actionColumn) &&
            (actionButtons === null || actionButtons === void 0 ? void 0 : actionButtons.columnPosition) === "right" &&
            getActionColumns()));
};
function getBody(col, item) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m;
    if (typeof item[col.field] === "boolean" &&
        ((_a = col.displayStyle) === null || _a === void 0 ? void 0 : _a.selected) === "text") {
        return item[col.field].toString();
    }
    else if (((_b = col.displayStyle) === null || _b === void 0 ? void 0 : _b.selected) === "tag") {
        return tagBodyTemplate(item[col.field], (_c = col.displayStyle) === null || _c === void 0 ? void 0 : _c.options);
    }
    else if (((_d = col.displayStyle) === null || _d === void 0 ? void 0 : _d.selected) === "number") {
        return numberBodyTemplate(item[col.field], (_e = col.displayStyle) === null || _e === void 0 ? void 0 : _e.options);
    }
    else if (((_f = col.displayStyle) === null || _f === void 0 ? void 0 : _f.selected) === "percentage") {
        return `${numberBodyTemplate(item[col.field], col.displayStyle.options)} %`;
    }
    else if (((_g = col.displayStyle) === null || _g === void 0 ? void 0 : _g.selected) === "currency") {
        return formatCurrency(item[col.field], col.displayStyle.options);
    }
    else if (((_h = col.displayStyle) === null || _h === void 0 ? void 0 : _h.selected) === "rating") {
        return formatRating(item[col.field], col.displayStyle.options);
    }
    else if (((_j = col.displayStyle) === null || _j === void 0 ? void 0 : _j.selected) === "progress") {
        return formatProgress(item[col.field], col.displayStyle.options);
    }
    else if (((_k = col.displayStyle) === null || _k === void 0 ? void 0 : _k.selected) === "link") {
        return formatExternalLink(item[col.field], col.displayStyle.options, item);
    }
    else if (((_l = col.displayStyle) === null || _l === void 0 ? void 0 : _l.selected) === "formattedDate") {
        return formatDate(item[col.field], col.displayStyle.options);
    }
    else if (((_m = col.displayStyle) === null || _m === void 0 ? void 0 : _m.selected) === "icon") {
        return booleanIconBodyTemplate(item[col.field]);
    }
    return item[col.field];
}
const getSeverity = (status) => {
    switch (status) {
        case "unqualified":
            return "danger";
        case "qualified":
            return "success";
        case "new":
            return "info";
        case "negotiation":
            return "warning";
        case "renewal":
            return null;
    }
    return "info";
};
const tagBodyTemplate = (data, options) => {
    return data ? (React.createElement(Tag, { value: data, severity: getSeverity(data), pt: { root: { className: (options === null || options === void 0 ? void 0 : options.roundedCorners) && "border-circle" } } })) : (data);
};
const numberBodyTemplate = (data, options) => {
    var _a;
    if (typeof data === "undefined") {
        return data;
    }
    if (!options) {
        return data;
    }
    const parsedData = parseFloat(data);
    if (typeof parsedData !== "number") {
        return data;
    }
    if (options.thousandSeparator) {
        const opts = {
            minimumFractionDigits: options.decimalPlaces,
            maximumFractionDigits: options.decimalPlaces
        };
        return new Intl.NumberFormat("en-US", options.decimalPlaces ? opts : {}).format(parsedData);
    }
    else
        return parsedData.toFixed((_a = options.decimalPlaces) !== null && _a !== void 0 ? _a : 0);
};
const booleanIconBodyTemplate = (data) => {
    if (typeof data === "string")
        return data === "true" ? (React.createElement("i", { className: "pi pi-check text-green-500" })) : (React.createElement("i", { className: "pi pi-times text-red-500" }));
    else
        return data ? (React.createElement("i", { className: "pi pi-check text-green-500" })) : (React.createElement("i", { className: "pi pi-times text-red-500" }));
};
const formatCurrency = (value, options) => {
    var _a;
    const intVal = parseInt(value);
    if (isNaN(intVal)) {
        return value;
    }
    return intVal.toLocaleString(navigator.languages[0], {
        style: "currency",
        currency: (_a = options === null || options === void 0 ? void 0 : options.currency) !== null && _a !== void 0 ? _a : "USD",
        currencyDisplay: "narrowSymbol"
    });
};
const formatRating = (value, options) => {
    return (React.createElement("div", { className: "flex flex-row" },
        React.createElement(Rating, { value: value, className: "mr-2", cancel: false, stars: options === null || options === void 0 ? void 0 : options.ratingAmount, pt: {
                onIcon: { style: { color: options === null || options === void 0 ? void 0 : options.color } },
                offIcon: { style: { color: options === null || options === void 0 ? void 0 : options.color } }
            } }),
        (options === null || options === void 0 ? void 0 : options.showRatingAmount) ? `${value}` : null));
};
const formatProgress = (value, options) => {
    return (React.createElement("div", { className: "flex flex-row" },
        React.createElement(ProgressBar, { className: "align-self-center mr-2 w-full", value: (options === null || options === void 0 ? void 0 : options.devideBy) ? (100 * value) / (options === null || options === void 0 ? void 0 : options.devideBy) : value, displayValueTemplate: () => React.createElement(React.Fragment, null), color: options === null || options === void 0 ? void 0 : options.color, style: { height: 6 } }),
        (options === null || options === void 0 ? void 0 : options.showNumber) ? (React.createElement("div", { className: "text-xs text-gray-500" }, value)) : null));
};
const formatExternalLink = (value, options, item) => {
    var _a, _b;
    const context = {
        rowData: item
    };
    const evaluated = typeof evaluate((_a = options === null || options === void 0 ? void 0 : options.url) !== null && _a !== void 0 ? _a : "", context) === "string"
        ? evaluate((_b = options === null || options === void 0 ? void 0 : options.url) !== null && _b !== void 0 ? _b : "", context)
        : "";
    return (React.createElement("a", { href: evaluated, target: "_blank" }, value));
};
const formatDate = (value, options = null) => {
    let result;
    if (typeof value === "string") {
        value = new Date(value);
    }
    if (!options) {
        result = value.toLocaleDateString();
        return result;
    }
    switch (options.dateFormat) {
        case "Local": {
            if (options.showTime) {
                result =
                    value.toLocaleDateString() +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toLocaleDateString();
            }
            break;
        }
        case "Friendly": {
            if (options.showTime) {
                result =
                    value.toLocaleDateString(undefined, { dateStyle: "long" }) +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toLocaleDateString(undefined, { dateStyle: "long" });
            }
            break;
        }
        case "US": {
            if (options.showTime) {
                result =
                    value.toLocaleDateString("us-US") +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toLocaleDateString("us-US");
            }
            break;
        }
        case "European": {
            if (options.showTime) {
                result =
                    value.toLocaleDateString("en-GB") +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toLocaleDateString("en-GB");
            }
            break;
        }
        case "ISO": {
            if (options.showTime) {
                result =
                    value.toISOString().slice(0, 10) +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toISOString().slice(0, 10);
            }
            break;
        }
        default: {
            if (options.showTime) {
                result =
                    value.toString() +
                        " " +
                        value.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: options.timeFormat === "12h"
                        });
            }
            else {
                result = value.toString();
            }
            break;
        }
    }
    return result;
};

export { C2DataTableChild as default };
//# sourceMappingURL=C2DataTableChild.js.map
